// CB:BEGIN add-ach-payment-support
import React, { useState, useEffect } from 'react';
import { Elements, CardElement, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import SharedHeaderCustomer from './SharedHeaderCustomer';
// CB:BEGIN homeowner-inline-help-import
import InlineHelpShortcut from './help/InlineHelpShortcut';
// CB:END homeowner-inline-help-import
import { useApi } from '../lib/useApi';
import { getApiUrl } from '../lib/getApiUrl';
import stripePromise from '../lib/stripe';
import { paymentApi } from '../lib/paymentApi';
// CB:END add-ach-payment-support

interface PaymentScreenProps {
  onNavigate: (screen: string) => void;
}

interface Project {
  id: string;
  address: string;
  status: string;
  projectType?: string;
  desiredStart?: string;
  acceptedBidCost?: number;
}

interface Bid {
  id: string;
  projectId: string;
  amount: number;
  isAccepted: boolean;
  createdAt: string;
  contractor?: {
    id: string;
    company?: string | null;
    user?: {
      email: string;
    };
  };
}

// Stripe Elements: use shared promise (no fake key; avoids HTTPS warning over HTTP)

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

// CB:BEGIN ach-payment-form-component
// ACH Payment Form Component using Stripe PaymentElement
const ACHPaymentForm: React.FC<{
  amount: number;
  onSuccess: (paymentIntentId?: string) => void;
  onError: (error: string) => void;
  processingFee: number;
  isDemoMode?: boolean;
  projectId?: string;
  onProcessingChange?: (isProcessing: boolean) => void;
}> = ({ amount, onSuccess, onError, processingFee, isDemoMode = false, projectId, onProcessingChange }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  // Notify parent of processing state changes
  React.useEffect(() => {
    onProcessingChange?.(isProcessing);
  }, [isProcessing, onProcessingChange]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    try {
      if (!projectId) {
        onError('Project ID is required');
        setIsProcessing(false);
        return;
      }
      const data = await paymentApi.createPaymentIntentForEscrow({ projectId });
      if (data.alreadyPaid) {
        onError('This escrow has already been paid.');
        setIsProcessing(false);
        return;
      }
      const clientSecret = data.clientSecret;
      if (!clientSecret) {
        onError('No client secret returned');
        setIsProcessing(false);
        return;
      }

      let { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      // Stale PI detected — clear and retry once
      if (confirmError && confirmError.message?.includes('No such payment_intent')) {
        console.warn('[PaymentScreen ACH] Stale PI detected, clearing and retrying:', confirmError.message);
        try {
          await paymentApi.clearStaleIntent({ projectId });
          const retryData = await paymentApi.createPaymentIntentForEscrow({ projectId });
          if (retryData.clientSecret) {
            const retryResult = await stripe.confirmPayment({
              elements,
              clientSecret: retryData.clientSecret,
              confirmParams: { return_url: window.location.href },
              redirect: 'if_required',
            });
            confirmError = retryResult.error ?? null;
            if (!confirmError && retryResult.paymentIntent) {
              paymentIntent = retryResult.paymentIntent;
            }
          }
        } catch (retryErr: any) {
          console.error('[PaymentScreen ACH] Retry after stale PI failed:', retryErr);
        }
      }

      if (confirmError) {
        const isKeyMismatch = confirmError.message?.includes('No such payment_intent');
        onError(isKeyMismatch
          ? 'Payment could not be confirmed — Stripe key mismatch. Contact support.'
          : (confirmError.message || 'Payment confirmation failed'));
        setIsProcessing(false);
        return;
      }

      onSuccess(paymentIntent?.id || data.stripePaymentIntentId, data.escrowId, data.totalAmountCents);
    } catch (error: any) {
      console.error('❌ ACH payment processing error:', error);
      const errorMessage = error?.message || error?.error || 'An unexpected error occurred';
      onError(errorMessage);
      setIsProcessing(false);
    }
  };

  // Event listener no longer needed - using form's native submit button

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: '20px' }}>
      <div className="form-group">
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
          Bank Account Information (ACH)
          {isDemoMode && <span style={{ color: '#10b981', fontSize: '12px', marginLeft: '8px' }}>(Demo)</span>}
        </label>
        {isDemoMode && (
          <div style={{
            background: '#f0fdf4',
            border: '1px solid #10b981',
            borderRadius: '6px',
            padding: '8px 12px',
            marginBottom: '8px',
            fontSize: '12px',
            color: '#059669'
          }}>
            🏦 <strong>Test ACH:</strong> Use any valid US bank account details for testing
          </div>
        )}
        <div style={{
          padding: '12px',
          border: isDemoMode ? '2px solid #10b981' : '1px solid #d1d5db',
          borderRadius: '6px',
          backgroundColor: isDemoMode ? '#f0fdf4' : '#ffffff'
        }}>
          <PaymentElement 
            options={{
              paymentMethodTypes: ['us_bank_account'],
            }}
          />
        </div>
        <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
          ACH payments typically take 3-5 business days to process. No processing fees apply.
        </p>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
        {!stripe && !isProcessing && (
          <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px', textAlign: 'center' }}>
            Payment form loading… If the button stays disabled, set <code style={{ fontSize: '12px' }}>VITE_STRIPE_PUBLISHABLE_KEY</code> in <code style={{ fontSize: '12px' }}>apps/web/.env</code> and restart the dev server.
          </p>
        )}
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="btn btn-primary"
          style={{
            maxWidth: '400px',
            width: '100%',
            padding: '14px 32px',
            fontSize: '16px',
            fontWeight: '600',
            borderRadius: '8px',
            cursor: !stripe || isProcessing ? 'not-allowed' : 'pointer',
            opacity: !stripe || isProcessing ? 0.6 : 1,
            transition: 'all 0.2s ease'
          }}
        >
          {isProcessing ? 'Processing...' : 'Pay with Bank Account'}
        </button>
      </div>
    </form>
  );
};
// CB:END ach-payment-form-component

// Payment Form Component using Stripe Elements (Card only)
const PaymentForm: React.FC<{
  amount: number; // This is the total with platform fee
  onSuccess: (paymentIntentId?: string, escrowId?: string, totalAmountCents?: number) => void;
  onError: (error: string) => void;
  processingFee: number;
  isDemoMode?: boolean;
  hideSubmitButton?: boolean; // CB:BEGIN hide-submit-button-option
  projectId?: string; // CB:BEGIN add-project-id-prop - Project ID for payment intent
  onProcessingChange?: (isProcessing: boolean) => void;
}> = ({ amount, onSuccess, onError, processingFee, isDemoMode = false, hideSubmitButton = false, projectId, onProcessingChange }) => {
  // CB:BEGIN fix-payment-form-amount
  // amount already includes platform fee, so total is amount + processingFee
  const totalAmount = amount + processingFee;
  // CB:END fix-payment-form-amount
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  // Notify parent of processing state changes
  React.useEffect(() => {
    onProcessingChange?.(isProcessing);
  }, [isProcessing, onProcessingChange]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      onError('Card element not found');
      setIsProcessing(false);
      return;
    }

    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        onError(error.message || 'Payment failed');
        setIsProcessing(false);
        return;
      }

      // CB:BEGIN dev-card-number-validation
      // In dev mode, only allow test card number 4242 4242 4242 4242
      const isDevMode = window.location.hostname === 'dev.corebids.com' || 
                        window.location.hostname === 'localhost' ||
                        import.meta.env.MODE === 'development';
      
      if (isDevMode && paymentMethod?.card) {
        // Check last 4 digits - test card ends in 4242
        const last4 = paymentMethod.card.last4;
        if (last4 !== '4242') {
          onError('In development mode, only the test card 4242 4242 4242 4242 is accepted. Please use the test card number.');
          setIsProcessing(false);
          return;
        }
      }
      // CB:END dev-card-number-validation

      if (!projectId) {
        onError('Project ID is required');
        setIsProcessing(false);
        return;
      }
      const data = await paymentApi.createPaymentIntentForEscrow({ projectId });
      if (data.alreadyPaid) {
        onError('This escrow has already been paid.');
        setIsProcessing(false);
        return;
      }
      const clientSecret = data.clientSecret;
      if (!clientSecret) {
        onError('No client secret returned');
        setIsProcessing(false);
        return;
      }

      const piId = clientSecret.split('_secret_')[0];
      console.log(`[PaymentScreen] Confirming PI ${piId} with publishable key starting: ${(import.meta as any).env?.VITE_STRIPE_PUBLISHABLE_KEY?.substring?.(0, 20) ?? 'NOT SET'}...`);
      let { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: cardElement },
      });

      // If Stripe.js can't find the PI, it means the stored PI is stale or was created
      // with a different Stripe account / mode (key mismatch). Clear it on the backend
      // and retry once with a fresh PI.
      if (confirmError && (
        confirmError.message?.includes('No such payment_intent') ||
        confirmError.message?.includes('payment_intent_unexpected_state')
      )) {
        console.warn('[PaymentScreen] Stale PI detected, clearing and retrying:', confirmError.message);
        try {
          await paymentApi.clearStaleIntent({ projectId });
          const retryData = await paymentApi.createPaymentIntentForEscrow({ projectId });
          if (retryData.clientSecret) {
            const retryResult = await stripe.confirmCardPayment(retryData.clientSecret, {
              payment_method: { card: cardElement },
            });
            confirmError = retryResult.error ?? null;
            if (!confirmError && retryResult.paymentIntent) {
              paymentIntent = retryResult.paymentIntent;
            }
          }
        } catch (retryErr: any) {
          console.error('[PaymentScreen] Retry after stale PI failed:', retryErr);
          // fall through to report the original error
        }
      }

      if (confirmError) {
        const isKeyMismatch = confirmError.message?.includes('No such payment_intent');
        const msg = isKeyMismatch
          ? 'Payment could not be confirmed — Stripe key mismatch between frontend and backend. Contact support or check that VITE_STRIPE_PUBLISHABLE_KEY (Vercel) and STRIPE_SECRET_KEY (Railway) are from the same Stripe account.'
          : (confirmError.message || 'Payment confirmation failed');
        onError(msg);
        setIsProcessing(false);
        return;
      }

      onSuccess(paymentIntent?.id || data.stripePaymentIntentId, data.escrowId, data.totalAmountCents);
    } catch (error: any) {
      // CB:BEGIN detailed-error-logging
      console.error('❌ Payment processing error:', error);
      const errorMessage = error?.message || error?.error || 'An unexpected error occurred';
      onError(errorMessage);
      setIsProcessing(false);
      // CB:END detailed-error-logging
    }
  };

  // Event listener no longer needed - using form's native submit button

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: '20px' }} data-stripe-form>
      <div className="form-group">
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
          Card Information
          {isDemoMode && <span style={{ color: '#10b981', fontSize: '12px', marginLeft: '8px' }}>(Demo)</span>}
        </label>
        {isDemoMode && (
          <div style={{
            background: '#f0fdf4',
            border: '1px solid #10b981',
            borderRadius: '6px',
            padding: '8px 12px',
            marginBottom: '8px',
            fontSize: '12px',
            color: '#059669'
          }}>
            💳 <strong>Test Card:</strong> 4242 4242 4242 4242 | <strong>Exp:</strong> 12/34 | <strong>CVC:</strong> 123
          </div>
        )}
        <div style={{
          padding: '12px',
          border: isDemoMode ? '2px solid #10b981' : '1px solid #d1d5db',
          borderRadius: '6px',
          backgroundColor: isDemoMode ? '#f0fdf4' : '#ffffff'
        }}>
          <CardElement options={cardElementOptions} />
        </div>
      </div>

      {/* CB:BEGIN conditional-submit-button */}
      {!hideSubmitButton && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '24px' }}>
          {!stripe && !isProcessing && (
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px', textAlign: 'center' }}>
              Payment form loading… If the button stays disabled, set <code style={{ fontSize: '12px' }}>VITE_STRIPE_PUBLISHABLE_KEY</code> in <code style={{ fontSize: '12px' }}>apps/web/.env</code> and restart the dev server.
            </p>
          )}
          <button
            type="submit"
            disabled={!stripe || isProcessing}
            className="btn btn-primary"
            style={{
              maxWidth: '400px',
              width: '100%',
              padding: '14px 32px',
              fontSize: '16px',
              fontWeight: '600',
              borderRadius: '8px',
              cursor: !stripe || isProcessing ? 'not-allowed' : 'pointer',
              opacity: !stripe || isProcessing ? 0.6 : 1,
              transition: 'all 0.2s ease'
            }}
          >
            {isProcessing ? 'Processing...' : `Pay $${(totalAmount / 100).toFixed(2)}`}
          </button>
        </div>
      )}
      {/* CB:END conditional-submit-button */}
    </form>
  );
};

const PaymentScreen: React.FC<PaymentScreenProps> = ({ onNavigate }) => {
  const { get, post } = useApi();
  const [project, setProject] = useState<Project | null>(null);
  const [bid, setBid] = useState<Bid | null>(null);
  const [loading, setLoading] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'eft' | 'wire'>('card');
  // Check if demo mode is enabled
  const isDemoMode = import.meta.env.VITE_DEMO_MODE === 'true' || 
                     import.meta.env.MODE === 'development' ||
                     window.location.hostname === 'localhost';

  // CB:BEGIN remove-manual-eft-form-state - Remove manual EFT form state (now using Stripe ACH)
  const [isSubmitting, setIsSubmitting] = useState(false);
  // CB:END remove-manual-eft-form-state
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stripeReady, setStripeReady] = useState<boolean | null>(null);
  useEffect(() => {
    stripePromise.then((s) => setStripeReady(!!s));
  }, []);

  useEffect(() => {
    fetchProjectData();
  }, []);

  const fetchProjectData = async () => {
    try {
      setLoading(true);
      
      // CB:BEGIN fix-payment-screen-projectid-validation
      // Prefer URL param (explicit navigation) over localStorage (may be stale)
      const urlProjectId = new URLSearchParams(window.location.search).get('projectId');
      const projectId = urlProjectId || localStorage.getItem('active_project_id');

      console.log('🔍 PaymentScreen: Looking for projectId:', {
        fromURL: urlProjectId,
        fromLocalStorage: localStorage.getItem('active_project_id'),
        final: projectId
      });
      
      if (!projectId || projectId === 'undefined' || projectId === 'null') {
        console.error('❌ PaymentScreen: No valid project ID found');
        setError('No project ID found. Please go back to your dashboard and try again.');
        setLoading(false);
        return;
      }
      // CB:END fix-payment-screen-projectid-validation

      // Fetch project details
      const projectResponse = await get(`/api/projects/${projectId}`);
      setProject(projectResponse);

      // CB:BEGIN fetch-accepted-bid-with-total
      // Fetch accepted bid - need to handle { bids } response format
      const bidsResponse = await get(`/api/projects/${projectId}/bids`);
      const bidsArray = Array.isArray(bidsResponse) ? bidsResponse : (bidsResponse?.bids || []);
      const acceptedBid = bidsArray.find((b: any) => b.isAccepted);
      setBid(acceptedBid);
      // CB:END fetch-accepted-bid-with-total

    } catch (error) {
      console.error('Error fetching project data:', error);
      setError('Failed to load project data');
    } finally {
      setLoading(false);
    }
  };

  const calculateProcessingFee = (amount: number) => {
    return paymentMethod === 'card' ? Math.round(amount * 0.03) : 0; // 3% for credit cards
  };

  // CB:BEGIN remove-manual-eft-handler - Manual EFT handler removed, now using Stripe ACH via ACHPaymentForm
  // The handleCardPaymentSuccess function now handles both card and ACH payments
  // CB:END remove-manual-eft-handler

  // Option A: Fund escrow via v2 API using escrowId and totalAmountCents from create-payment-intent response
  const handlePaymentSuccess = async (
    stripePaymentIntentId?: string,
    escrowId?: string,
    totalAmountCents?: number
  ) => {
    try {
      if (!project?.id) {
        setError('Missing project');
        return;
      }
      if (!escrowId || totalAmountCents == null || totalAmountCents <= 0) {
        setError('Missing escrow funding details. Please try again.');
        return;
      }
      const idempotencyKeyFund = `escrow_fund_${escrowId}`;
      await post(`/api/escrow/v2/${escrowId}/fund`, {
        amountCents: totalAmountCents,
        idempotencyKeyFund,
        stripePaymentIntentId: stripePaymentIntentId ?? undefined,
      });
      setPaymentAmount(totalAmountCents);
      setPaymentSuccess(true);
      try {
        window.dispatchEvent(
          new CustomEvent('cb:payment-succeeded', {
            detail: { projectId: project.id, method: paymentMethod === 'card' ? 'CARD' : 'ACH' },
          })
        );
      } catch {}
    } catch (error: any) {
      console.error('Error funding escrow:', error);
      const errorMessage =
        error?.message || error?.error || 'Failed to complete escrow funding. Please contact support.';
      setError(errorMessage);
    }
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  if (loading) {
    return (
      <div className="container">
        <SharedHeaderCustomer onNavigate={onNavigate} />
        <div className="content" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p style={{ marginTop: '16px', color: '#6b7280' }}>Loading payment information...</p>
        </div>
      </div>
    );
  }

  if (!project || !bid) {
    return (
      <div className="container">
        <SharedHeaderCustomer onNavigate={onNavigate} />
        <div className="content" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <h2>No Payment Information Found</h2>
          <p style={{ color: '#6b7280', marginBottom: '24px' }}>
            No accepted bid found for this project. Please contact support.
          </p>
          <button 
            className="btn btn-primary"
            onClick={() => onNavigate('customer-dashboard')}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (paymentSuccess) {
    const amountDisplay = `$${paymentAmount ? (paymentAmount / 100).toFixed(2) : '0.00'}`;
    const steps = [
      { icon: '🔨', label: 'Work begins', detail: 'Your contractor will be notified and can schedule a start date.' },
      { icon: '📋', label: 'Milestone approvals', detail: 'Funds are released in stages as each phase of work is completed.' },
      { icon: '🛡️', label: 'Funds held securely', detail: 'Your money stays in escrow until you approve each milestone.' },
    ];
    return (
      <div className="container">
        <SharedHeaderCustomer onNavigate={onNavigate} />
        <div className="content" style={{ maxWidth: '560px', margin: '0 auto', padding: '48px 20px' }}>

          {/* Success card */}
          <div style={{
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
            marginBottom: '24px',
          }}>
            {/* Green header band */}
            <div style={{
              background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
              padding: '36px 32px',
              textAlign: 'center',
            }}>
              <div style={{
                width: '64px', height: '64px',
                background: 'rgba(255,255,255,0.15)',
                borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px',
                fontSize: '28px',
              }}>
                ✓
              </div>
              <h2 style={{ margin: '0 0 8px', fontSize: '22px', fontWeight: '700', color: '#ffffff', letterSpacing: '-0.3px' }}>
                {isDemoMode ? 'Demo Payment Confirmed' : 'Payment Confirmed'}
              </h2>
              <p style={{ margin: 0, fontSize: '14px', color: 'rgba(255,255,255,0.8)' }}>
                Your escrow deposit was received
              </p>
            </div>

            {/* Amount row */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '20px 32px',
              borderBottom: '1px solid #f3f4f6',
            }}>
              <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>Amount deposited</span>
              <span style={{ fontSize: '24px', fontWeight: '700', color: '#111827', letterSpacing: '-0.5px' }}>
                {amountDisplay}
              </span>
            </div>

            {/* Status row */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 32px',
              borderBottom: '1px solid #f3f4f6',
            }}>
              <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>Escrow status</span>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                background: '#f0fdf4', color: '#15803d',
                fontSize: '13px', fontWeight: '600',
                padding: '4px 12px', borderRadius: '20px',
                border: '1px solid #bbf7d0',
              }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />
                Funded &amp; Secured
              </span>
            </div>

            {/* What's next */}
            <div style={{ padding: '24px 32px' }}>
              <p style={{ margin: '0 0 16px', fontSize: '13px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                What happens next
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {steps.map((step, i) => (
                  <div key={i} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                    <div style={{
                      width: '36px', height: '36px', flexShrink: 0,
                      background: '#f9fafb', border: '1px solid #e5e7eb',
                      borderRadius: '10px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '16px',
                    }}>
                      {step.icon}
                    </div>
                    <div>
                      <p style={{ margin: '0 0 2px', fontSize: '14px', fontWeight: '600', color: '#111827' }}>{step.label}</p>
                      <p style={{ margin: 0, fontSize: '13px', color: '#6b7280', lineHeight: '1.5' }}>{step.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CTA */}
          <button
            onClick={() => {
              if (project?.id) {
                window.location.href = `/projects/${project.id}`;
              } else {
                onNavigate('customer-dashboard');
              }
            }}
            style={{
              width: '100%',
              padding: '14px',
              fontSize: '15px',
              fontWeight: '600',
              background: '#111827',
              color: '#ffffff',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              letterSpacing: '-0.1px',
            }}
          >
            View Project →
          </button>
        </div>
      </div>
    );
  }

  // CB:BEGIN display-total-with-platform-fee
  // Calculate total homeowner pays: contractor bid + 10% platform fee
  const contractorBidCents = bid?.amount || 0;
  const platformFeeCents = Math.round(contractorBidCents * 0.10);
  const totalWithPlatformFee = contractorBidCents + platformFeeCents;
  const processingFee = calculateProcessingFee(totalWithPlatformFee);
  const totalAmount = totalWithPlatformFee + processingFee;
  // CB:END display-total-with-platform-fee

  return (
    <div className="container">
      <SharedHeaderCustomer onNavigate={onNavigate} />
      <div className="content" style={{ paddingTop: '12px', paddingBottom: '16px' }}>
        {/* CB:BEGIN homeowner-inline-help-payment */}
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <InlineHelpShortcut
            onNavigate={onNavigate}
            sectionId="payments-deposits"
            label="Need help with payments or deposits?"
          />
        </div>
        {/* CB:END homeowner-inline-help-payment */}

        {/* CB:BEGIN financing-banner */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          background: '#fffbeb',
          border: '1px solid #fcd34d',
          borderRadius: '8px',
          padding: '10px 16px',
          marginBottom: '14px',
          flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '16px' }}>💳</span>
          <span style={{ fontSize: '14px', color: '#92400e', fontWeight: '500', flex: 1 }}>
            Need help covering this payment?
          </span>
          <button
            onClick={(e) => {
              e.preventDefault();
              window.open(`${window.location.origin}/financing`, '_blank', 'noopener,noreferrer');
            }}
            style={{
              background: '#d97706',
              border: 'none',
              color: 'white',
              fontSize: '13px',
              fontWeight: '600',
              padding: '6px 14px',
              borderRadius: '6px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            View financing options
          </button>
        </div>
        {/* CB:END financing-banner */}

        {/* Two-column layout */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 5fr) minmax(0, 7fr)',
          gap: '14px',
          alignItems: 'start',
        }}>

          {/* LEFT COLUMN: Project Details + Payment Summary */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

            {/* Project Details */}
            <div style={{
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: '10px',
              padding: '16px',
            }}>
              <h3 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: '600', color: '#111827' }}>
                Project Details
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {bid?.contractor && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                    <span style={{ color: '#6b7280', fontSize: '13px', flexShrink: 0 }}>Contractor</span>
                    <span style={{ fontWeight: '600', fontSize: '13px', textAlign: 'right' }}>
                      {bid.contractor.company || 'Contractor'}
                    </span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                  <span style={{ color: '#6b7280', fontSize: '13px', flexShrink: 0 }}>Address</span>
                  <span style={{ fontWeight: '600', fontSize: '13px', textAlign: 'right' }}>{project.address}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                  <span style={{ color: '#6b7280', fontSize: '13px', flexShrink: 0 }}>Project Type</span>
                  <span style={{ fontWeight: '600', fontSize: '13px', textAlign: 'right' }}>{project.projectType || 'Roof'}</span>
                </div>
              </div>
            </div>

            {/* Payment Summary */}
            <div style={{
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: '10px',
              padding: '16px',
            }}>
              <h3 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: '600', color: '#111827' }}>
                Payment Summary
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {/* CB:BEGIN show-total-in-breakdown - Total includes contractor bid + platform fee */}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6b7280', fontSize: '13px' }}>Project Total</span>
                  <span style={{ fontSize: '13px' }}>{formatCurrency(totalWithPlatformFee)}</span>
                </div>
                {/* CB:END show-total-in-breakdown */}
                {processingFee > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280', fontSize: '13px' }}>Processing Fee (3%)</span>
                    <span style={{ fontSize: '13px' }}>{formatCurrency(processingFee)}</span>
                  </div>
                )}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  borderTop: '1px solid #e5e7eb',
                  paddingTop: '8px',
                  marginTop: '2px',
                }}>
                  <span style={{ fontWeight: '700', fontSize: '15px' }}>Total Due</span>
                  <span style={{ fontWeight: '700', fontSize: '15px', color: '#10b981' }}>{formatCurrency(totalAmount)}</span>
                </div>
              </div>
            </div>

            {/* Secure payment note */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280', fontSize: '12px', paddingLeft: '2px' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              <span>Funds held in escrow until work is approved</span>
            </div>
          </div>

          {/* RIGHT COLUMN: Payment Method + Form */}
          <div style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '10px',
            padding: '16px',
          }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: '600', color: '#111827' }}>
              Payment Method
            </h3>

            {/* Stripe error */}
            {stripeReady === false && (
              <div style={{
                background: '#fef2f2',
                border: '1px solid #dc2626',
                borderRadius: '6px',
                padding: '10px 14px',
                marginBottom: '12px',
                color: '#991b1b',
                fontSize: '13px',
              }}>
                <strong>Stripe key required.</strong> Set <code style={{ background: '#fee2e2', padding: '1px 4px', borderRadius: '3px' }}>VITE_STRIPE_PUBLISHABLE_KEY=pk_test_...</code> in <code style={{ background: '#fee2e2', padding: '1px 4px', borderRadius: '3px' }}>apps/web/.env</code> and restart.
              </div>
            )}

            {/* Method tabs */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
              {(['card', 'eft', 'wire'] as const).map((m) => {
                const labels: Record<string, string> = { card: 'Credit/Debit Card', eft: 'ACH Transfer', wire: 'Wire Transfer' };
                const active = paymentMethod === m;
                return (
                  <button
                    key={m}
                    onClick={() => setPaymentMethod(m)}
                    style={{
                      padding: '8px 16px',
                      border: active ? '2px solid #2563eb' : '2px solid #e5e7eb',
                      borderRadius: '7px',
                      background: active ? '#eff6ff' : '#f9fafb',
                      color: active ? '#2563eb' : '#6b7280',
                      fontWeight: active ? '600' : '400',
                      fontSize: '13px',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {labels[m]}
                  </button>
                );
              })}
            </div>

            {/* Processing fee notice */}
            {paymentMethod === 'card' && processingFee > 0 && (
              <div style={{
                background: '#fef3c7',
                border: '1px solid #fcd34d',
                borderRadius: '6px',
                padding: '6px 10px',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}>
                <span style={{ fontSize: '11px' }}>⚠️</span>
                <span style={{ fontSize: '12px', color: '#92400e' }}>
                  3% card processing fee included ({formatCurrency(processingFee)})
                </span>
              </div>
            )}

            {/* Demo Mode Banner */}
            {isDemoMode && (
              <div style={{
                background: '#ecfdf5',
                border: '1px solid #6ee7b7',
                borderRadius: '6px',
                padding: '8px 12px',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <span style={{ fontSize: '14px' }}>🧪</span>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#065f46' }}>Demo Mode — no real charges</div>
                  <div style={{ fontSize: '12px', color: '#047857' }}>Test card: 4242 4242 4242 4242</div>
                </div>
              </div>
            )}

            {/* Payment Forms */}
            {paymentMethod === 'card' ? (
              <Elements stripe={stripePromise}>
                <PaymentForm
                  amount={totalWithPlatformFee}
                  processingFee={processingFee}
                  onSuccess={handlePaymentSuccess}
                  onError={setError}
                  isDemoMode={isDemoMode}
                  projectId={project?.id} // CB:BEGIN pass-project-id - Pass project ID to PaymentForm
                  hideSubmitButton={false}
                  onProcessingChange={setIsSubmitting}
                />
              </Elements>
            ) : paymentMethod === 'eft' ? (
              // CB:BEGIN use-stripe-ach-payment-element - Use Stripe PaymentElement for ACH payments
              <Elements stripe={stripePromise}>
                <ACHPaymentForm
                  amount={totalWithPlatformFee}
                  processingFee={processingFee}
                  onSuccess={handlePaymentSuccess}
                  onError={setError}
                  isDemoMode={isDemoMode}
                  projectId={project?.id}
                  onProcessingChange={setIsSubmitting}
                />
              </Elements>
              // CB:END use-stripe-ach-payment-element
            ) : (
              /* Wire Transfer — call for instructions */
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .84h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.63a16 16 0 006.29 6.29l1.45-1.45a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>Wire Transfer</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Call us for personalized wire instructions</div>
                  </div>
                </div>
                <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 7, padding: '12px 14px', marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Amount to Wire</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#111827' }}>{formatCurrency(totalWithPlatformFee)}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{project?.address || 'Your project'}</div>
                </div>
                <a
                  href="tel:+19109222371"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '11px 20px', background: '#2563eb', color: '#fff', borderRadius: 7, fontSize: 14, fontWeight: 600, textDecoration: 'none', marginBottom: 8 }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .84h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.63a16 16 0 006.29 6.29l1.45-1.45a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                  </svg>
                  Call for Wire Instructions
                </a>
                <p style={{ fontSize: 11, color: '#9ca3af', textAlign: 'center', marginBottom: 8 }}>Mon – Fri 8 am – 6 pm CT · (910) 922-2371</p>
                <a
                  href="mailto:billing@corebids.com?subject=Wire Transfer Instructions"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '9px 20px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, fontWeight: 500, color: '#374151', textDecoration: 'none', background: '#fff' }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  billing@corebids.com
                </a>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div style={{
                background: '#fee2e2',
                border: '1px solid #dc2626',
                borderRadius: '7px',
                padding: '10px 14px',
                marginTop: '12px',
                color: '#dc2626',
                fontSize: '13px',
              }}>
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentScreen;